import { FormErrors } from '@openx/components-core/lib/FormErrors/FormErrors';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { Lock } from '@openx/components-core/lib/Lock/Lock';
import { PageHeader } from '@openx/components-header/PageHeader';
import { isInventoryFormatIncompatibleWithRbo } from '@openx/components-targeting/utils/isTargetingRboCompatible';
import type { PackageFormState, PackageWithTargeting } from '@openx/types/package';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { usePackageContext } from '../../utils/context/PackageContext';
import { determineFormType, getNewPath } from '../../utils/formNavigation';
import { PackageForm } from './PackageForm';
import { type ShowConfirmationModalParams, useEditConfirmationModal } from './useEditConfirmationModal';
import { useRelatedDeals } from './useRelatedDeals';
import { computeDealsWithActions } from './utils/computeDealsWithActions';
import { getDiff, packageToFormState } from './utils/formStateConverter';

const patchUrlTargetingDiff = (
  diff: ReturnType<typeof getDiff>,
  newUrlTargeting: PackageFormState['targeting']['url_targeting']
): void => {
  const urlTargeting = diff.url_targeting;

  if (urlTargeting?.domain_app_inventory_operator && newUrlTargeting !== null) {
    diff.url_targeting = newUrlTargeting;
  }

  if (!urlTargeting?.domain_targeting_option && !urlTargeting?.type) {
    return;
  }

  if (!urlTargeting.inventorylist_id && newUrlTargeting?.inventorylist_id) {
    urlTargeting.inventorylist_id = newUrlTargeting.inventorylist_id;
  }

  if (!urlTargeting.urls && newUrlTargeting?.urls) {
    urlTargeting.urls = newUrlTargeting.urls;
  }

  if (
    urlTargeting.domain_app_inventory_operator === undefined &&
    newUrlTargeting?.domain_app_inventory_operator !== undefined
  ) {
    urlTargeting.domain_app_inventory_operator = newUrlTargeting.domain_app_inventory_operator;
  }
};

export type PackageEditFormProps = {
  goBack: () => void;
  packageId: string;
  titlePrefix: 'DSP UI' | 'OXControl' | 'OpenXSelect';
  openXAgentTools?: (isSubmitting: boolean, formValues: PackageFormState) => JSX.Element;
};

export const PackageEditForm = ({
  goBack,
  packageId,
  titlePrefix,
  openXAgentTools,
}: PackageEditFormProps): JSX.Element => {
  const {
    usePackageFetch,
    useUpdateForm,
    useRelatedAccountsFetch,
    useCloneForm,
    useDealDisableRbo,
    usePackageRetargetingDealsValidation,
  } = usePackageContext();
  const { isLoading: relatedDealsLoading, ...relatedDeals } = useRelatedDeals(packageId);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { clonePackage, isCloning } = useCloneForm();
  const { pckg, pckgLoading, remove: clearPckgCache } = usePackageFetch(packageId);
  const { isUpdating, updatePackage } = useUpdateForm(packageId);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const { relatedAccounts } = useRelatedAccountsFetch(pckg ? [pckg as PackageWithTargeting] : [], {
    enabled: !!pckg,
  });

  const [resetFormData, setResetFormData] = useState<PackageFormState>();

  const { showConfirmationModal } = useEditConfirmationModal();
  const { disableDealsRbo, isDisabling } = useDealDisableRbo(pckg?.id ?? '');

  const historyState = location.state;
  const accountUid = pckg?.account_uid;
  const account = accountUid ? relatedAccounts[accountUid] : null;
  const currency = account?.currency;
  const pckgFromHistory = historyState?.package;
  // Prefetch DSP enrichment in the background; do not block the edit form Loader on it.
  const { isLoading: retargetingDealsValidationLoading, getIncompatibleDeals } =
    usePackageRetargetingDealsValidation(accountUid);

  const pckgState = useMemo(() => (pckg ? packageToFormState(pckg) : ({} as PackageFormState)), [pckg]);

  useEffect(() => {
    if (pckgState && pckgFromHistory) {
      setResetFormData(pckgFromHistory);
    }
  }, [pckgState, pckgFromHistory]);

  const onCancel = useCallback((): void => {
    if (historyState?.redirectPath) {
      const { redirectPath, ...nextState } = historyState;

      navigate(redirectPath, { replace: true, state: nextState });
      return;
    }

    const type = determineFormType(historyState);

    if (type) {
      const newPath = getNewPath(historyState, type);
      navigate(newPath, { replace: true, state: historyState });
    } else {
      goBack();
    }
  }, [goBack, historyState, navigate]);

  const handleClonePackage = useCallback(
    async (values: PackageFormState, setError: UseFormSetError<PackageFormState>): Promise<void> => {
      if (!pckg) {
        return;
      }
      const diff = getDiff(values, pckgState);
      await clonePackage({ diff, initialValues: pckgState, pckg, setError, setSubmitErrors, values });
    },
    [clonePackage, pckg, pckgState]
  );

  const onSubmit = useCallback(
    (
      values: PackageFormState,
      setError: UseFormSetError<PackageFormState>,
      disableResults?: boolean,
      isConfirmed?: boolean
    ) => {
      if (!pckg) {
        return;
      }

      const hasRetargetingSegment = !!values.targeting?.[TargetingFields.RETARGETING]?.val;

      // Fail closed: do not save retargeting while DSP enrichment is still loading.
      if (!isConfirmed && hasRetargetingSegment && retargetingDealsValidationLoading) {
        setSubmitErrors([t('Validating Retargeting DSP compatibility. Please try again in a moment.')]);
        return;
      }

      const incompatibleRetargetingDeals =
        !isConfirmed &&
        getIncompatibleDeals({
          targeting: values.targeting,
          deals: relatedDeals.deals,
          hasInaccessibleDeals: relatedDeals.hasInaccessibleDeals,
        });

      if (incompatibleRetargetingDeals) {
        showConfirmationModal({
          deals: incompatibleRetargetingDeals.deals,
          hasMore: incompatibleRetargetingDeals.hasMore,
          mode: 'remove_retargeting',
          isRBODynamic: false,
          confirmationButtonColor: 'error',
          confirmationLabel: t('Remove Retargeting & Save'),
          handleClonePackage: () => handleClonePackage(values, setError),
          onConfirmed: () => {
            const valuesWithoutRetargeting: PackageFormState = {
              ...values,
              targeting: {
                ...values.targeting,
                [TargetingFields.RETARGETING]: null,
              },
            };

            // Keep isConfirmed false so RBO / related-deals confirmation can still run next.
            onSubmit(valuesWithoutRetargeting, setError, false, false);
          },
        });
        return;
      }

      setSubmitErrors([]);

      const needsConfirmation = !isConfirmed && relatedDeals.count > 0;
      if (needsConfirmation) {
        const isIncompatibleTargeting = isInventoryFormatIncompatibleWithRbo(
          values.targeting[TargetingFields.INVENTORY_FORMAT]
        );

        const { dealsWithAction, allDealsToDisable, dealsToReset, hasMore } = computeDealsWithActions(
          relatedDeals.deals,
          isIncompatibleTargeting,
          relatedDeals.hasInaccessibleDeals
        );

        const shouldDisableOnConfirm = isIncompatibleTargeting && allDealsToDisable.length > 0;

        const baseDialogOptions: ShowConfirmationModalParams = {
          mode: 'default',
          isRBODynamic: relatedDeals.isWithRBOD && dealsToReset.length > 0,
          hasMore,
          deals: dealsWithAction,
          handleClonePackage: () => handleClonePackage(values, setError),
          onConfirmed: () => onSubmit(values, setError, false, true),
        };

        const dialogOptions: ShowConfirmationModalParams = shouldDisableOnConfirm
          ? {
              ...baseDialogOptions,
              mode: 'disable_rbo',
              confirmationButtonColor: 'error',
              confirmationLabel: t('Disable Engagement Optimization & Save'),
              onConfirmed: () => onSubmit(values, setError, true, true),
            }
          : baseDialogOptions;

        showConfirmationModal(dialogOptions);
        return;
      }

      const diff = getDiff(values, pckgState);
      patchUrlTargetingDiff(diff, values.targeting[TargetingFields.URL_TARGETING]);

      if (disableResults) {
        const { allDealsToDisable } = computeDealsWithActions(
          relatedDeals.deals,
          true,
          relatedDeals.hasInaccessibleDeals
        );

        return disableDealsRbo(allDealsToDisable)
          .then(() =>
            updatePackage({
              clearCache: clearPckgCache,
              diff,
              initialValues: pckgState,
              setError,
              setSubmitErrors,
              values,
            })
          )
          .catch(error => {
            setSubmitErrors([error.message]);
          });
      }

      updatePackage({
        clearCache: clearPckgCache,
        diff,
        initialValues: pckgState,
        setError,
        setSubmitErrors,
        values,
      });
    },
    [
      t,
      pckg,
      relatedDeals.count,
      relatedDeals.isWithRBOD,
      relatedDeals.deals,
      relatedDeals.hasInaccessibleDeals,
      pckgState,
      updatePackage,
      clearPckgCache,
      showConfirmationModal,
      handleClonePackage,
      disableDealsRbo,
      getIncompatibleDeals,
      retargetingDealsValidationLoading,
    ]
  );

  const loading = pckgLoading || !pckg || !currency || relatedDealsLoading;

  const PreTitleIconComponent = pckg?.locked_internal ? <Lock divider /> : undefined;

  return (
    <PageHeader title={t('Edit')} titlePrefix={titlePrefix} PreTitleIconComponent={PreTitleIconComponent}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormErrors errors={submitErrors} />
          <PackageForm
            initialValues={pckgState}
            goBack={onCancel}
            isSubmitting={isUpdating || isCloning || isDisabling}
            currency={currency}
            isEdit
            onSubmit={onSubmit}
            resetFormData={resetFormData}
            openXAgentTools={openXAgentTools}
          />
        </>
      )}
    </PageHeader>
  );
};
